import React, { useState, useEffect } from 'react';
import { Helmet } from "react-helmet-async";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiService } from '@/services/ApiService';

interface FormField {
  id: string;
  name: string;
  type: string;
  component: string;
  validation?: string;
  defaultValue?: string;
  placeholder?: string;
  description?: string;
}

interface FormReference {
  id: string;
  title: string;
  description: string;
  storageLocation: string;
  fields: FormField[];
}

interface UIElement {
  type: string;
  variant?: string;
  usage: string;
  location: string;
  component: string;
}

const DevSystem: React.FC = () => {
  const isMobile = useIsMobile();
  const formReferences: FormReference[] = [
    {
      id: "login-form",
      title: "Login Form",
      description: "Used for operator login",
      storageLocation: "localStorage (demo), Supabase Auth (production)",
      fields: [
        {
          id: "email-field",
          name: "email",
          type: "email",
          component: "Input",
          validation: "Required, valid email format",
          placeholder: "your@email.com",
          description: "User's email address"
        },
        {
          id: "password-field",
          name: "password",
          type: "password",
          component: "Input",
          validation: "Required",
          placeholder: "••••••••",
          description: "User's password"
        }
      ]
    },
    {
      id: "signup-form",
      title: "Signup Form",
      description: "Used for operator registration",
      storageLocation: "localStorage (demo), Supabase Auth (production)",
      fields: [
        {
          id: "business-name-field",
          name: "businessName",
          type: "text",
          component: "Input",
          validation: "Required",
          placeholder: "Your Business Name",
          description: "Operator's business name"
        },
        {
          id: "email-field",
          name: "email",
          type: "email",
          component: "Input",
          validation: "Required, valid email format",
          placeholder: "your@email.com",
          description: "User's email address"
        },
        {
          id: "password-field",
          name: "password",
          type: "password",
          component: "Input",
          validation: "Required, min length 8",
          placeholder: "••••••••",
          description: "User's password"
        },
        {
          id: "confirm-password-field",
          name: "confirmPassword",
          type: "password",
          component: "Input",
          validation: "Must match password field",
          placeholder: "••••••••",
          description: "Password confirmation"
        },
        {
          id: "selected-plan-field",
          name: "selectedPlan",
          type: "radio",
          component: "RadioGroup",
          defaultValue: "premium",
          description: "Selected pricing plan (free or premium)"
        }
      ]
    },
    {
      id: "service-form",
      title: "Service Management Form",
      description: "Used for adding/editing services in the operator dashboard",
      storageLocation: "React state (demo), Supabase DB (production)",
      fields: [
        {
          id: "service-name-field",
          name: "name",
          type: "text",
          component: "Input",
          placeholder: "Service Name",
          description: "Name of the service"
        },
        {
          id: "service-type-field",
          name: "type",
          type: "select",
          component: "Select",
          description: "Type of service (Sauna, Plunge, Ice Bath, Combination)"
        },
        {
          id: "service-duration-field",
          name: "duration",
          type: "number",
          component: "Input",
          description: "Duration of service in minutes"
        },
        {
          id: "service-price-field",
          name: "price",
          type: "number",
          component: "Input",
          description: "Price of the service in Euros"
        },
        {
          id: "service-public-field",
          name: "isPublic",
          type: "boolean",
          component: "Checkbox",
          description: "Whether the service is public"
        },
        {
          id: "service-live-sensor-field",
          name: "enableLiveSensor",
          type: "boolean",
          component: "Checkbox",
          description: "Whether to enable live sensor for the service"
        },
        {
          id: "service-categories-field",
          name: "categories",
          type: "array",
          component: "Tags",
          description: "Categories assigned to the service"
        }
      ]
    },
    {
      id: "tags-categories",
      title: "Tags & Categories Management",
      description: "Used for managing tags and categories in the operator dashboard",
      storageLocation: "React state (demo), Supabase DB (production)",
      fields: [
        {
          id: "service-type-field",
          name: "serviceType",
          type: "array",
          component: "TagInput",
          description: "Types of services offered"
        },
        {
          id: "format-field",
          name: "format",
          type: "array",
          component: "TagInput",
          description: "Format of services"
        },
        {
          id: "experience-field",
          name: "experience",
          type: "array",
          component: "TagInput",
          description: "Experience categories"
        },
        {
          id: "addons-field",
          name: "addons",
          type: "array", 
          component: "TagInput",
          description: "Additional services offered"
        },
        {
          id: "audience-field",
          name: "audience",
          type: "array", 
          component: "TagInput",
          description: "Target audience"
        }
      ]
    },
    {
      id: "contact-form",
      title: "Contact Form",
      description: "Used for visitor inquiries",
      storageLocation: "Form submissions handled by backend",
      fields: [
        {
          id: "contact-name-field",
          name: "name",
          type: "text",
          component: "Input",
          validation: "Required",
          placeholder: "Your Name",
          description: "Visitor's name"
        },
        {
          id: "contact-email-field",
          name: "email",
          type: "email",
          component: "Input",
          validation: "Required, valid email format",
          placeholder: "your@email.com",
          description: "Visitor's email address"
        },
        {
          id: "contact-subject-field",
          name: "subject",
          type: "text",
          component: "Input",
          validation: "Required",
          placeholder: "Subject",
          description: "Message subject"
        },
        {
          id: "contact-message-field",
          name: "message",
          type: "textarea",
          component: "Textarea",
          validation: "Required, min length 10",
          placeholder: "Your message...",
          description: "Message content"
        },
        {
          id: "contact-type-field",
          name: "inquiryType",
          type: "select",
          component: "Select",
          validation: "Required",
          description: "Type of inquiry"
        }
      ]
    },
    {
      id: "directory-filter-form",
      title: "Directory Filters",
      description: "Used for filtering directory listings",
      storageLocation: "React state",
      fields: [
        {
          id: "filter-search-field",
          name: "search",
          type: "text",
          component: "Input",
          placeholder: "Search...",
          description: "Search filter"
        },
        {
          id: "filter-type-field",
          name: "type",
          type: "select",
          component: "Select",
          defaultValue: "all",
          description: "Filter by venue type"
        },
        {
          id: "filter-country-field",
          name: "selectedCountry",
          type: "select",
          component: "Select",
          defaultValue: "ireland",
          description: "Filter by country"
        },
        {
          id: "filter-counties-field",
          name: "selectedCounties",
          type: "array",
          component: "MultiSelect",
          description: "Filter by counties/regions"
        }
      ]
    }
  ];

  const uiElements: UIElement[] = [
    {
      type: "Button",
      variant: "default",
      usage: "Primary action buttons",
      location: "Throughout the application",
      component: "@/components/ui/button.tsx"
    },
    {
      type: "Button",
      variant: "outline",
      usage: "Secondary action buttons",
      location: "Throughout the application",
      component: "@/components/ui/button.tsx"
    },
    {
      type: "Button",
      variant: "ghost",
      usage: "Navigation links",
      location: "Navbar, sidebar",
      component: "@/components/ui/button.tsx"
    },
    {
      type: "Card",
      usage: "Content containers",
      location: "Dashboard, forms",
      component: "@/components/ui/card.tsx"
    },
    {
      type: "Input",
      usage: "Text input fields",
      location: "Forms",
      component: "@/components/ui/input.tsx"
    },
    {
      type: "Select",
      usage: "Dropdown selection",
      location: "Forms, filters",
      component: "@/components/ui/select.tsx"
    },
    {
      type: "Checkbox",
      usage: "Boolean input",
      location: "Forms, toggles",
      component: "@/components/ui/checkbox.tsx"
    },
    {
      type: "RadioGroup",
      usage: "Option selection",
      location: "Forms",
      component: "@/components/ui/radio-group.tsx"
    },
    {
      type: "Badge",
      usage: "Status indicators, tags",
      location: "Lists, cards",
      component: "@/components/ui/badge.tsx"
    },
    {
      type: "Tabs",
      usage: "Content organization",
      location: "Operator dashboard",
      component: "@/components/ui/tabs.tsx"
    },
    {
      type: "Calendar",
      usage: "Date selection",
      location: "Forms, booking",
      component: "@/components/ui/calendar.tsx"
    }
  ];

  const backendTodos = [
    {
      id: "auth-integration",
      title: "Auth Integration",
      description: "Complete Laravel Sanctum integration for authentication",
      priority: "High",
      status: "In Progress"
    },
    {
      id: "api-endpoints",
      title: "API Endpoints",
      description: "Finalize RESTful API endpoints for directory listings",
      priority: "High",
      status: "In Progress"
    },
    {
      id: "image-optimization",
      title: "Image Optimization",
      description: "Implement server-side image processing for uploads",
      priority: "Medium",
      status: "Not Started"
    },
    {
      id: "notification-system",
      title: "Notification System",
      description: "Create notification service for operators",
      priority: "Medium",
      status: "Not Started"
    },
    {
      id: "geocoding-service",
      title: "Geocoding Service",
      description: "Implement address to coordinates conversion service",
      priority: "High",
      status: "In Progress"
    },
    {
      id: "booking-api",
      title: "Booking API",
      description: "Develop booking management API for premium operators",
      priority: "Low",
      status: "Not Started"
    }
  ];

  const backendRequirements = [
    {
      category: "Server",
      requirements: [
        "PHP 8.2+ with Laravel 10.x",
        "Composer for dependency management",
        "MySQL 8.0+ or PostgreSQL 14+ database",
        "Redis for caching and queue processing",
        "Minimum 1GB RAM for web server"
      ]
    },
    {
      category: "Services",
      requirements: [
        "Laravel Cloud account with production environment",
        "Mailgun API access for transactional emails",
        "Stripe API integration for subscription management",
        "S3-compatible storage for media files",
        "OpenCage API key for geocoding services"
      ]
    },
    {
      category: "Environment",
      requirements: [
        "Laravel .env configuration for all environments",
        "Doppler secrets management for API keys",
        "CORS configuration for frontend access",
        "SSL certificates for all domains",
        "Rate limiting for public API endpoints"
      ]
    },
    {
      category: "Deployment",
      requirements: [
        "CI/CD pipeline with GitHub Actions",
        "Automatic database backups",
        "Blue/green deployment capability",
        "Staging environment for testing",
        "Performance monitoring with New Relic or similar"
      ]
    }
  ];

  const apiEndpoints = [
    {
      category: "Authentication",
      endpoints: [
        {
          method: "POST",
          path: "auth/login",
          description: "Login with email and password, returns access token",
          params: "email, password"
        },
        {
          method: "POST",
          path: "auth/register",
          description: "Register a new user account",
          params: "name, email, password, password_confirmation"
        },
        {
          method: "POST",
          path: "auth/logout",
          description: "Invalidate user's current access token",
          params: "Bearer token (header)"
        },
        {
          method: "GET",
          path: "auth/user",
          description: "Get authenticated user profile",
          params: "Bearer token (header)"
        }
      ]
    },
    {
      category: "Operator Portal",
      endpoints: [
        {
          method: "GET",
          path: "operator/business",
          description: "View operator's owned listing",
          params: "Bearer token (header)"
        },
        {
          method: "PUT",
          path: "operator/business",
          description: "Update business information",
          params: "Bearer token (header), business data"
        },
        {
          method: "GET",
          path: "operator/stats",
          description: "View stats: views, clicks, plan information",
          params: "Bearer token (header)"
        },
        {
          method: "POST",
          path: "operator/upgrade",
          description: "Trigger Stripe plan upgrade",
          params: "Bearer token (header), plan_id"
        }
      ]
    },
    {
      category: "Admin Panel",
      endpoints: [
        {
          method: "GET",
          path: "admin/businesses/pending",
          description: "View listings awaiting approval",
          params: "Bearer token (header)"
        },
        {
          method: "POST",
          path: "admin/businesses/{id}/approve",
          description: "Approve a pending listing",
          params: "Bearer token (header)"
        },
        {
          method: "GET",
          path: "admin/operators",
          description: "View all operator accounts",
          params: "Bearer token (header)"
        },
        {
          method: "GET",
          path: "admin/reports",
          description: "View site metrics (signups, upgrades, etc)",
          params: "Bearer token (header)"
        }
      ]
    },
    {
      category: "Public Directory API",
      endpoints: [
        {
          method: "GET",
          path: "businesses",
          description: "Get all approved business listings",
          params: "filters (optional)"
        },
        {
          method: "GET",
          path: "businesses/{id}",
          description: "Get details for a specific business",
          params: "business_id"
        },
        {
          method: "GET",
          path: "counties",
          description: "Get list of counties with business counts",
          params: "none"
        },
        {
          method: "POST",
          path: "contact",
          description: "Submit contact form",
          params: "name, email, message, business_id (optional)"
        }
      ]
    }
  ];

  const systemFlows = [
    {
      title: "Email Integration",
      description: "All system-generated emails (contact form, claim confirmation, approval alerts) are sent using Mailgun or SMTP. Configuration is managed through Laravel's .env file.",
      steps: [
        "Frontend form submission to contact endpoint or similar endpoint",
        "Laravel handles validation and email formatting",
        "Email sent via Mailgun or configured SMTP provider",
        "Success/failure notification returned to frontend"
      ],
      config: [
        "MAIL_MAILER=smtp",
        "MAIL_HOST=[mail_host]",
        "MAIL_PORT=587",
        "MAIL_USERNAME=[mail_username]",
        "MAIL_PASSWORD=[password]",
        "MAIL_ENCRYPTION=tls",
        "MAIL_FROM_ADDRESS=[from_email]"
      ]
    },
    {
      title: "Payments & Stripe Integration",
      description: "Stripe is used to manage subscription plans. Laravel Cashier handles the integration between Laravel and Stripe.",
      steps: [
        "Operator clicks upgrade in dashboard",
        "Frontend calls operator/upgrade endpoint",
        "Backend creates Stripe Checkout session",
        "User completes payment on Stripe hosted page",
        "Stripe webhook notifies our system of completed payment",
        "User account upgraded to new plan"
      ],
      config: [
        "STRIPE_KEY=[publishable_key]",
        "STRIPE_SECRET=[secret_key]",
        "STRIPE_WEBHOOK_SECRET=[webhook_secret]"
      ]
    },
    {
      title: "Authentication Flow",
      description: "Laravel Sanctum provides token-based authentication with Spatie RBAC for role management.",
      steps: [
        "User submits credentials",
        "Laravel validates and issues Sanctum token",
        "Token stored in localStorage or HTTP-only cookie",
        "Subsequent API calls include token in Authorization header",
        "Role-based permissions control access to admin/operator endpoints"
      ],
      config: [
        "SESSION_DRIVER=cookie",
        "SANCTUM_STATEFUL_DOMAINS=[domain_list]",
        "SPA_URL=[spa_url]"
      ]
    }
  ];

  // New subscriptionFlow data for the new section
  const subscriptionFlow = {
    title: "Subscription Flow",
    description: "Complete documentation of the user subscription journey",
    steps: [
      {
        title: "Account Creation (Signup)",
        description: "Users start by creating an account through a three-step signup form",
        details: [
          "Step 1: Enter personal information (name, email, password)",
          "Step 2: Enter business information (business name, business type, country)",
          "Step 3: Review information and accept terms & conditions",
          "This creates a basic user account with the free plan by default"
        ]
      },
      {
        title: "Subscription Selection",
        description: "Users view and select from available subscription plans",
        details: [
          "Three plan options are displayed: Free, Basic ($19/month), and Premium ($49/month)",
          "Each plan shows different features and benefits",
          "The Basic plan offers a 7-day free trial",
          "The Premium plan offers a 14-day free trial"
        ]
      },
      {
        title: "Plan Upgrade Process",
        description: "The process for upgrading to a paid plan",
        details: [
          "When a user clicks to upgrade, if not logged in, they are redirected to the login page",
          "If logged in, the upgrade process begins immediately",
          "For Basic/Premium plans with trials, no credit card is required initially",
          "The selected plan is activated immediately through the subscription service"
        ]
      },
      {
        title: "Post-Subscription Flow",
        description: "What happens after a user subscribes to a plan",
        details: [
          "After subscription, users are redirected to the subscription success page",
          "The success page confirms their subscription and shows next steps",
          "Users can then manage their subscription from their account dashboard",
          "Users can view their current plan, features, limits, and trial status"
        ]
      }
    ],
    planDetails: [
      {
        name: "Free Plan",
        price: "$0/month",
        features: [
          "Basic profile",
          "Directory listing",
          "Single image upload",
          "Up to 2 service listings",
          "Booking link"
        ],
        limits: {
          serviceListings: 2,
          galleryImages: 0
        },
        trial: "No trial (free forever)"
      },
      {
        name: "Premium Plan",
        price: "$49/month",
        features: [
          "Everything in Free plan",
          "Up to 3 gallery images",
          "Up to 5 service listings",
          "Full control of tags & filters",
          "Google rating display",
          "Basic analytics"
        ],
        limits: {
          serviceListings: 5,
          galleryImages: 3
        },
        trial: "14-day free trial"
      }
    ],
    implementation: {
      components: [
        { name: "PricingPlans.tsx", purpose: "Displays available subscription plans with features and pricing" },
        { name: "SubscriptionStatus.tsx", purpose: "Shows current subscription status, plan details, and management options" },
        { name: "FeatureGuard.tsx", purpose: "Conditionally renders components based on subscription plan level" },
        { name: "UpgradePrompt.tsx", purpose: "Encourages users to upgrade when they reach plan limits" }
      ],
      services: [
        { name: "subscription.ts", purpose: "Service that handles subscription status, upgrades, cancellations, and resumptions" },
        { name: "useSubscription.js", purpose: "React hook that provides subscription functionality to components" }
      ],
      pages: [
        { name: "Subscription.tsx", purpose: "Main subscription management page" },
        { name: "SubscriptionSuccess.tsx", purpose: "Confirmation page after successful subscription" }
      ]
    }
  };

  return (
    <div className="container py-6 md:py-10">
      <Helmet>
        <title>Developer System | SaunaDip</title>
        <meta name="description" content="Developer system reference for SaunaDip" />
      </Helmet>

      <div className="mb-6 md:mb-10">
        <h1 className="text-3xl md:text-4xl font-bold mb-2">Developer System Reference</h1>
        <p className="text-gray-500">
          A comprehensive reference of all forms, fields, and UI elements used throughout the SaunaDip application.
        </p>
      </div>

      <Tabs defaultValue="tech-stack" className="w-full">
        <ScrollArea className="w-full">
          <TabsList className="mb-6 flex flex-wrap">
            <TabsTrigger value="tech-stack">Tech Stack</TabsTrigger>
            <TabsTrigger value="forms">Forms & Data</TabsTrigger>
            <TabsTrigger value="ui">UI Elements</TabsTrigger>
            <TabsTrigger value="colors">Color System</TabsTrigger>
            <TabsTrigger value="subscription">Subscription Flow</TabsTrigger>
            <TabsTrigger value="backend">Backend</TabsTrigger>
            <TabsTrigger value="api">API Reference</TabsTrigger>
            <TabsTrigger value="backend-api">Backend API & Integration</TabsTrigger>
          </TabsList>
        </ScrollArea>

        {/* Tech Stack Tab */}
        <TabsContent value="tech-stack">
          <div className="grid gap-6">
            <h2 className="text-2xl font-bold">Tech Stack</h2>
            <p className="text-gray-500 mb-4">
              This section details all the technologies used in the SaunaDip platform and their purpose in supporting the application.
            </p>
            
            <Accordion type="multiple" className="w-full">
              <AccordionItem value="frontend-stack">
                <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                  🖥️ Frontend Stack
                </AccordionTrigger>
                <AccordionContent>
                  <Card>
                    <CardContent className="pt-6 overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr>
                            <th className="text-left py-2 px-4 font-medium">Tech</th>
                            <th className="text-left py-2 px-4 font-medium">Purpose</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">React</td>
                            <td className="py-2 px-4">Core UI library for the web app, enabling component-based architecture for maintainable and reusable UI elements</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">TypeScript</td>
                            <td className="py-2 px-4">Strong typing to improve code maintainability, reduce bugs, and provide better IDE autocompletion and documentation</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Tailwind CSS</td>
                            <td className="py-2 px-4">Utility-first styling approach for rapid UI development with consistent design tokens and responsive layouts</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">shadcn/ui</td>
                            <td className="py-2 px-4">UI component library for fast, accessible design with consistent look-and-feel across the platform while maintaining customizability</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Lucide React</td>
                            <td className="py-2 px-4">Icon library for SVG icons that are lightweight, accessible, and consistent across the UI</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">react-router-dom</td>
                            <td className="py-2 px-4">Routing between pages with support for protected routes, dynamic paths, and query parameters</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">date-fns</td>
                            <td className="py-2 px-4">Lightweight date formatting and manipulation for booking systems, availability calendars, and date displays</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Tanstack React Query</td>
                            <td className="py-2 px-4">Server state management and API data fetching with caching, background updates, and optimistic UI updates</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Vite</td>
                            <td className="py-2 px-4">Fast development server and build tool for efficient hot module replacement and production builds</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Astro</td>
                            <td className="py-2 px-4">Static marketing/blog pages for SEO-optimized content with minimal JavaScript, deployed separately from the main app</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Leaflet + MapLibre</td>
                            <td className="py-2 px-4">Interactive map showing sauna and plunge listings with clustering, filtering, and location-based search</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Percy.io</td>
                            <td className="py-2 px-4">Visual testing to catch unintended UI changes across different viewport sizes and browser environments</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Playwright</td>
                            <td className="py-2 px-4">Automated UI testing for critical user flows including login, signup, listings, and booking processes</td>
                          </tr>
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="backend-stack">
                <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                  ⚙️ Backend & API
                </AccordionTrigger>
                <AccordionContent>
                  <Card>
                    <CardContent className="pt-6 overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr>
                            <th className="text-left py-2 px-4 font-medium">Tech</th>
                            <th className="text-left py-2 px-4 font-medium">Purpose</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Laravel</td>
                            <td className="py-2 px-4">Core backend framework providing RESTful API endpoints, database ORM, middleware, and authentication services (transitioning from Lovable.dev)</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Laravel Cloud</td>
                            <td className="py-2 px-4">Laravel's official hosting platform for simplified deployment, scaling, and management with built-in monitoring and performance optimization</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">SQLite</td>
                            <td className="py-2 px-4">Lightweight embedded DB for fast prototyping during development, with easy migration path to production databases</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Supabase (Auth)</td>
                            <td className="py-2 px-4">Used for user auth with social login options and security features (to be phased out or replaced with Laravel Sanctum)</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Doppler</td>
                            <td className="py-2 px-4">Secrets and config management across environments ensuring secure handling of API keys, connection strings, and other sensitive data</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Cursor.sh</td>
                            <td className="py-2 px-4">AI pair programmer assisting developers with code generation, refactoring, and debugging (used optionally, not required)</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Mailgun</td>
                            <td className="py-2 px-4">Transactional emails service for reliable delivery of welcome emails, listing updates, booking confirmations, and outreach campaigns</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Stripe</td>
                            <td className="py-2 px-4">Subscription billing with 14-day trial offer, payment processing, and invoice management for operator subscriptions</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Cloudflare Pages</td>
                            <td className="py-2 px-4">Static site deployment for the marketing site with global CDN, automated builds from Git, and custom domain setup</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">UptimeRobot</td>
                            <td className="py-2 px-4">Domain uptime monitoring with alerts and status pages to ensure platform availability and quick response to outages</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Sentry</td>
                            <td className="py-2 px-4">Error tracking across frontend and backend with source maps, performance monitoring, and alert notifications</td>
                          </tr>
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="ai-automation">
                <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                  🧠 AI & Automation
                </AccordionTrigger>
                <AccordionContent>
                  <Card>
                    <CardContent className="pt-6 overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr>
                            <th className="text-left py-2 px-4 font-medium">Tool</th>
                            <th className="text-left py-2 px-4 font-medium">Purpose</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">OpenAI API</td>
                            <td className="py-2 px-4">Business summary generation, automated category tagging, and AI enrichment of listings to improve search relevance and data quality</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Google Apps Script</td>
                            <td className="py-2 px-4">Automated scraping of potential operators, populating Google Sheets with data, and triggering Mailgun outreach campaigns</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">GitHub Actions</td>
                            <td className="py-2 px-4">CI/CD pipelines for automated testing, data scraping tasks, database commits, and deployment of frontend and backend services</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Apify</td>
                            <td className="py-2 px-4">Web scraping platform for extracting Instagram bio information, follower counts, and Google Places details for venue enrichment</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">OpenCage API</td>
                            <td className="py-2 px-4">Geocoding service to convert physical addresses into latitude/longitude coordinates for map display and location-based searching</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">BetterStack (Logtail)</td>
                            <td className="py-2 px-4">Unified log aggregation and monitoring solution for centralized visibility into application performance and error tracking</td>
                          </tr>
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="crm-admin">
                <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                  📒 CRM, Admin, and Outreach
                </AccordionTrigger>
                <AccordionContent>
                  <Card>
                    <CardContent className="pt-6 overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr>
                            <th className="text-left py-2 px-4 font-medium">Tool</th>
                            <th className="text-left py-2 px-4 font-medium">Purpose</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Google Sheets</td>
                            <td className="py-2 px-4">Backend operator CRM and data enrichment staging area with collaborative editing and automated integrations</td>
                          </tr>
                          <tr className="border-b border-gray-100">
                            <td className="py-2 px-4 font-medium">Lovable.dev</td>
                            <td className="py-2 px-4">AI-powered web app development platform used for initial prototyping and UI development</td>
                          </tr>
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </TabsContent>

        {/* Forms & Data Tab */}
        <TabsContent value="forms">
          <div className="grid gap-6">
            <h2 className="text-2xl font-bold">Forms & Data References</h2>
            <p className="text-gray-500 mb-4">
              This section documents all forms used throughout the application, their fields, and data handling details.
            </p>
            
            <Accordion type="multiple" className="w-full">
              {formReferences.map((form) => (
                <AccordionItem key={form.id} value={form.id}>
                  <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                    {form.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <Card>
                      <CardHeader>
                        <CardTitle>{form.title}</CardTitle>
                        <p className="text-sm text-gray-500">{form.description}</p>
                        <Badge variant="outline" className="mt-2">Storage: {form.storageLocation}</Badge>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Name</TableHead>
                              <TableHead>Type</TableHead>
                              <TableHead>Component</TableHead>
                              <TableHead>Validation</TableHead>
                              <TableHead>Description</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {form.fields.map((field) => (
                              <TableRow key={field.id}>
                                <TableCell className="font-medium">{field.name}</TableCell>
                                <TableCell>{field.type}</TableCell>
                                <TableCell>{field.component}</TableCell>
                                <TableCell>{field.validation || "None"}</TableCell>
                                <TableCell>{field.description}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </TabsContent>

        {/* UI Elements Tab */}
        <TabsContent value="ui">
          <div className="grid gap-6">
            <h2 className="text-2xl font-bold">UI Elements</h2>
            <p className="text-gray-500 mb-4">
              This section documents all UI components used throughout the application.
            </p>
            
            <Card>
              <CardContent className="pt-6">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Component</TableHead>
                      <TableHead>Variant</TableHead>
                      <TableHead>Usage</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Component Path</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {uiElements.map((element, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{element.type}</TableCell>
                        <TableCell>{element.variant || "default"}</TableCell>
                        <TableCell>{element.usage}</TableCell>
                        <TableCell>{element.location}</TableCell>
                        <TableCell>
                          <code className="bg-muted rounded px-1 py-0.5 text-xs">
                            {element.component}
                          </code>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Color System Tab */}
        <TabsContent value="colors">
          <div className="grid gap-6">
            <h2 className="text-2xl font-bold">Color System</h2>
            <p className="text-gray-500 mb-4">
              The color palette and system used throughout the application.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Primary Colors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded bg-primary"></div>
                      <div>
                        <p className="font-medium">Primary</p>
                        <p className="text-xs text-muted-foreground">For main CTAs and highlights</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded bg-primary-foreground"></div>
                      <div>
                        <p className="font-medium">Primary Foreground</p>
                        <p className="text-xs text-muted-foreground">For text on primary backgrounds</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Secondary Colors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded bg-secondary"></div>
                      <div>
                        <p className="font-medium">Secondary</p>
                        <p className="text-xs text-muted-foreground">For secondary UI elements</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded bg-secondary-foreground"></div>
                      <div>
                        <p className="font-medium">Secondary Foreground</p>
                        <p className="text-xs text-muted-foreground">For text on secondary backgrounds</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Accent Colors</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded bg-accent"></div>
                      <div>
                        <p className="font-medium">Accent</p>
                        <p className="text-xs text-muted-foreground">For accented UI elements</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded bg-accent-foreground"></div>
                      <div>
                        <p className="font-medium">Accent Foreground</p>
                        <p className="text-xs text-muted-foreground">For text on accent backgrounds</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
        
        {/* New Subscription Flow Tab */}
        <TabsContent value="subscription">
          <div className="grid gap-6">
            <h2 className="text-2xl font-bold">Subscription Flow</h2>
            <p className="text-gray-500 mb-4">
              Complete documentation of the user subscription journey from signup to plan management.
            </p>
            
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>User Journey Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {/* Journey visualization */}
                  <div className="hidden md:flex justify-between mb-8 relative">
                    {subscriptionFlow.steps.map((step, idx) => (
                      <div key={idx} className="flex flex-col items-center w-1/4 px-2 relative z-10">
                        <div className="w-12 h-12 rounded-full bg-sauna-orange flex items-center justify-center text-white font-bold mb-2">
                          {idx + 1}
                        </div>
                        <p className="text-center font-semibold">{step.title}</p>
                      </div>
                    ))}
                    {/* Connecting line */}
                    <div className="absolute top-6 left-0 w-full h-0.5 bg-gray-200 -z-0"></div>
                  </div>
                  
                  {/* Steps details */}
                  <Accordion type="single" collapsible className="w-full">
                    {subscriptionFlow.steps.map((step, idx) => (
                      <AccordionItem key={idx} value={`step-${idx}`}>
                        <AccordionTrigger className="text-lg font-semibold hover:no-underline">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-sauna-orange flex items-center justify-center text-white font-bold">
                              {idx + 1}
                            </div>
                            {step.title}
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className="pl-11">
                            <p className="text-gray-600 mb-3">{step.description}</p>
                            <ul className="list-disc pl-5 space-y-1">
                              {step.details.map((detail, detailIdx) => (
                                <li key={detailIdx} className="text-gray-700">{detail}</li>
                              ))}
                            </ul>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle>Subscription Plans</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Plan</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Trial</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscriptionFlow.planDetails.map((plan, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{plan.name}</TableCell>
                          <TableCell>{plan.price}</TableCell>
                          <TableCell>{plan.trial}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Feature Limits by Plan</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Plan</TableHead>
                        <TableHead>Service Listings</TableHead>
                        <TableHead>Gallery Images</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subscriptionFlow.planDetails.map((plan, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{plan.name}</TableCell>
                          <TableCell>{plan.limits.serviceListings}</TableCell>
                          <TableCell>{plan.limits.galleryImages}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Implementation Components</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-semibold mb-3">Components</h3>
                    <ul className="space-y-3">
                      {subscriptionFlow.implementation.components.map((component, idx) => (
                        <li key={idx} className="border-b border-gray-100 pb-2">
                          <div className="font-medium text-sauna-navy">{component.name}</div>
                          <div className="text-sm text-gray-600">{component.purpose}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-semibold mb-3">Services & Pages</h3>
                    <ul className="space-y-3 mb-6">
                      {subscriptionFlow.implementation.services.map((service, idx) => (
                        <li key={idx} className="border-b border-gray-100 pb-2">
                          <div className="font-medium text-sauna-navy">{service.name}</div>
                          <div className="text-sm text-gray-600">{service.purpose}</div>
                        </li>
                      ))}
                    </ul>
                    
                    <h3 className="font-semibold mb-3">Pages</h3>
                    <ul className="space-y-3">
                      {subscriptionFlow.implementation.pages.map((page, idx) => (
                        <li key={idx} className="border-b border-gray-100 pb-2">
                          <div className="font-medium text-sauna-navy">{page.name}</div>
                          <div className="text-sm text-gray-600">{page.purpose}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Subscription Code Flow</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-auto">
                  <pre className="bg-muted p-4 rounded-md text-sm">
{`// Example subscription upgrade flow
// From PricingPlans.tsx

const handleSubscription = (planId: string) => {
  if (!isAuthenticated()) {
    // Redirect to login if not authenticated
    navigate("/login", { state: { from: "/subscription" } });
    return;
  }
  
  // Call the subscription service to upgrade
  upgrade(planId);
};

// From subscription service
async upgrade(planId: string): Promise<{ checkout_url: string; trial_days: number }> {
  // In a real implementation, this would create a Stripe Checkout session
  // For demonstration, we'll just update the user's plan in localStorage
  localStorage.setItem('user_plan', planId);
  
  const plan = mockPlans.find(p => p.id === planId);
  
  // In a real app, this would return a Stripe checkout URL
  return {
    checkout_url: '/subscription-success',
    trial_days: plan?.trial_days || 0
  };
}`}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Backend Tab */}
        <TabsContent value="backend">
          <div className="grid gap-6">
            <h2 className="text-2xl font-bold">Backend Development</h2>
            <p className="text-gray-500 mb-4">
              Backend development resources, todos, and requirements.
            </p>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Backend Todos</CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Task</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {backendTodos.map((todo) => (
                        <TableRow key={todo.id}>
                          <TableCell className="font-medium">{todo.title}</TableCell>
                          <TableCell>{todo.description}</TableCell>
                          <TableCell>
                            <Badge variant={todo.priority === "High" ? "destructive" : todo.priority === "Medium" ? "default" : "outline"}>
                              {todo.priority}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={todo.status === "In Progress" ? "default" : "outline"}>
                              {todo.status}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Backend Requirements</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Accordion type="multiple">
                    {backendRequirements.map((req, index) => (
                      <AccordionItem key={index} value={`req-${index}`}>
                        <AccordionTrigger className="px-6">
                          {req.category}
                        </AccordionTrigger>
                        <AccordionContent className="px-6">
                          <ul className="space-y-2 text-sm">
                            {req.requirements.map((requirement, reqIndex) => (
                              <li key={reqIndex} className="flex items-start gap-2">
                                <div className="mt-1 min-w-4">•</div>
                                <div>{requirement}</div>
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* API Reference Tab */}
        <TabsContent value="api">
          <div className="grid gap-6">
            <h2 className="text-2xl font-bold">API Reference</h2>
            <p className="text-gray-500 mb-4">
              Documentation of available API endpoints.
            </p>
            
            <Accordion type="multiple" className="w-full">
              {apiEndpoints.map((category, index) => (
                <AccordionItem key={index} value={`api-${index}`}>
                  <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                    {category.category}
                  </AccordionTrigger>
                  <AccordionContent>
                    <Card>
                      <CardContent className="pt-6">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Method</TableHead>
                              <TableHead>Path</TableHead>
                              <TableHead>Description</TableHead>
                              <TableHead>Parameters</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {category.endpoints.map((endpoint, endpointIndex) => (
                              <TableRow key={endpointIndex}>
                                <TableCell>
                                  <Badge className={
                                    endpoint.method === "GET" ? "bg-blue-500" :
                                    endpoint.method === "POST" ? "bg-green-500" :
                                    endpoint.method === "PUT" ? "bg-amber-500" :
                                    endpoint.method === "DELETE" ? "bg-red-500" : ""
                                  }>
                                    {endpoint.method}
                                  </Badge>
                                </TableCell>
                                <TableCell className="font-mono text-xs">
                                  {endpoint.path}
                                </TableCell>
                                <TableCell>{endpoint.description}</TableCell>
                                <TableCell className="font-mono text-xs">
                                  {endpoint.params}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </TabsContent>

        {/* Backend API & Integration Tab */}
        <TabsContent value="backend-api">
          <div className="grid gap-6">
            <h2 className="text-2xl font-bold">Backend Integration</h2>
            <p className="text-gray-500 mb-4">
              Documentation of integration processes and system flows.
            </p>
            
            <Accordion type="multiple" className="w-full">
              {systemFlows.map((flow, index) => (
                <AccordionItem key={index} value={`flow-${index}`}>
                  <AccordionTrigger className="text-xl font-semibold hover:no-underline">
                    {flow.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <Card>
                      <CardHeader>
                        <p className="text-sm text-gray-600">{flow.description}</p>
                      </CardHeader>
                      <CardContent>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-semibold mb-2">Process Steps</h4>
                            <ol className="space-y-2 list-decimal list-inside">
                              {flow.steps.map((step, stepIndex) => (
                                <li key={stepIndex} className="text-sm">{step}</li>
                              ))}
                            </ol>
                          </div>
                          <div>
                            <h4 className="font-semibold mb-2">Configuration</h4>
                            <pre className="bg-muted p-4 rounded-md overflow-auto text-xs">
                              {flow.config.join('\n')}
                            </pre>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DevSystem;
